// ============= Imports =============
import { AuthContextProps } from '@/contexts/AuthContext';
import { tokenManager } from '@tw/tw-component-library';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_CORE_URL;
const API_ENDPOINT = '/api/Workspace';
const LITIUM_SCOPE = import.meta.env.VITE_LITIUM_CORE_SCOPE;

const HEADERS = {};

// ============= Types =============

export interface DeleteUserFromWorkspaceProps {
  getAccessToken: AuthContextProps['getAccessToken'];
  workspaceId: string;
  userId: string;
}

// ============= Utility Functions =============
/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

// ============= Main Function =============
/**
 * Deletes a user from a workspace
 * @param getAccessToken Function to acquire access token
 * @param workspaceId Workspace ID
 * @param userId User ID
 * @returns Promise with API response or error
 */
export const deleteUserFromWorkspace = async ({
  getAccessToken,
  workspaceId,
  userId,
}: DeleteUserFromWorkspaceProps): Promise<any> => {
  try {
    console.log('🔄 Deleting user from workspace...');

    // Token acquisition with required scopes
    const token = await getAccessToken([LITIUM_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    // URL construction
    const url = `${BASE_URL}${API_ENDPOINT}/${workspaceId}/user/${userId}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    // API request
    const response = await fetch(convertURI, {
      method: 'DELETE',
      headers: createHeaders(token),
    });

    // Response handling
    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Some DELETE endpoints may not return JSON
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    console.log(`✅ User deleted from workspace successfully: ${data}`);

    return data;
  } catch (error) {
    console.error('🔴 Error deleting user from workspace:', error);
    throw error;
  }
};
